import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AD_CONSENT_EVENT, getAdConsent, setAdConsent } from "@/lib/ad-consent";
import { useAuth } from "@/hooks/useAuth";

/**
 * One-time advertising-consent choice for third-party ad pixels (Roku).
 * Shown only to signed-out visitors who haven't chosen yet. First-party
 * product analytics are unaffected either way. Dismisses permanently once
 * the visitor picks Accept or Decline.
 */
export function AdConsentBanner() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => setVisible(getAdConsent() === "unset");
    update();
    window.addEventListener(AD_CONSENT_EVENT, update);
    return () => window.removeEventListener(AD_CONSENT_EVENT, update);
  }, []);

  if (isLoading || user || !visible) return null;

  return (
    <div
      role="region"
      aria-label={t("consent.title")}
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto max-w-lg rounded-2xl border border-rule bg-card p-4 shadow-lg space-y-3">
        <p className="text-sm font-semibold text-ink">{t("consent.title")}</p>
        <p className="text-xs text-ink-muted leading-relaxed">{t("consent.body")}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAdConsent("granted")}
            className="flex-1 h-11 rounded-xl bg-neon-pink text-white text-sm font-semibold transition-opacity hover:opacity-90"
          >
            {t("consent.accept")}
          </button>
          <button
            type="button"
            onClick={() => setAdConsent("denied")}
            className="flex-1 h-11 rounded-xl border border-rule text-sm font-medium text-ink transition-colors hover:bg-paper"
          >
            {t("consent.decline")}
          </button>
        </div>
      </div>
    </div>
  );
}
